import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { ILCELER, PARA_BIRIMLERI, ARAC_TIPLERI } from '@/lib/constants';
import type { Rezervasyon } from '@/types/rezervasyon';
import RezervasyonTablosu from './components/RezervasyonTablosu';

const DURUMLAR = ['Yeni Rezervasyon', 'Onaylandı', 'Araç Atandı', 'Transfer Tamamlandı', 'İptal Edildi', 'Ödeme Bekliyor'];

function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}
function getWeekStartStr() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
}
function getMonthStartStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function SkeletonRow() {
  return (
    <tr className="border-b border-vip-gold/5">
      {Array.from({ length: 15 }).map((_, i) => (
        <td key={i} className="py-3 px-3">
          <div className="h-3 bg-white/5 rounded animate-pulse" style={{ width: `${50 + (i % 4) * 15}%` }}></div>
        </td>
      ))}
    </tr>
  );
}

export default function RezervasyonlarPage() {
  useAuthGuard();

  const [rezervasyonlar, setRezervasyonlar] = useState<Rezervasyon[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const [durumYukleniyor, setDurumYukleniyor] = useState<number | null>(null);
  const [silmeOnay, setSilmeOnay] = useState<number | null>(null);
  const [siliniyor, setSiliniyor] = useState(false);

  const [arama, setArama] = useState('');
  const [durumFilter, setDurumFilter] = useState('');
  const [ilceFilter, setIlceFilter] = useState('');
  const [pbFilter, setPbFilter] = useState('');
  const [aracFilter, setAracFilter] = useState('');
  const [tarihFilter, setTarihFilter] = useState('');
  const [tarihBitis, setTarihBitis] = useState('');
  const [aktifTarihBtn, setAktifTarihBtn] = useState('');
  const [siralama, setSiralama] = useState({ kolon: 'gelis_tarihi', yon: 'desc' as 'asc' | 'desc' });

  const fetchRezervasyonlar = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    const { data, error } = await supabase
      .from('reservations')
      .select('*')
      .order('gelis_tarihi', { ascending: false })
      .order('gelis_saati', { ascending: true });

    if (error) {
      setHata('Rezervasyonlar yüklenemedi: ' + error.message);
    } else {
      setRezervasyonlar((data as Rezervasyon[]) || []);
    }
    setYukleniyor(false);
  }, []);

  useEffect(() => {
    fetchRezervasyonlar();
  }, [fetchRezervasyonlar]);

  const handleDurumGuncelle = async (id: number, yeniDurum: string) => {
    setDurumYukleniyor(id);
    const { error } = await supabase
      .from('reservations')
      .update({ durum: yeniDurum })
      .eq('id', id);
    if (!error) {
      setRezervasyonlar(prev =>
        prev.map(r => (r.id === id ? { ...r, durum: yeniDurum } : r))
      );
    } else {
      setHata('Durum güncellenemedi: ' + error.message);
    }
    setDurumYukleniyor(null);
  };

  const handleSil = async () => {
    if (silmeOnay === null) return;
    const idToDelete = silmeOnay;
    setSiliniyor(true);
    setSilmeOnay(null);

    const { data, error } = await supabase.functions.invoke('manage-reservations', {
      body: { action: 'delete', id: idToDelete },
    });

    setSiliniyor(false);

    if (error || !data?.success) {
      setHata('Rezervasyon silinemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
      await fetchRezervasyonlar();
      return;
    }
    setRezervasyonlar(prev => prev.filter(r => r.id !== idToDelete));
  };

  const handleSiralama = (kolon: string) => {
    setSiralama(prev => ({
      kolon,
      yon: prev.kolon === kolon && prev.yon === 'asc' ? 'desc' : 'asc',
    }));
  };

  const setHizliTarih = (tip: string) => {
    const today = getTodayStr();
    if (tip === 'bugun') {
      setTarihFilter(today); setTarihBitis(today);
    } else if (tip === 'hafta') {
      setTarihFilter(getWeekStartStr()); setTarihBitis(today);
    } else if (tip === 'ay') {
      setTarihFilter(getMonthStartStr()); setTarihBitis(today);
    } else {
      setTarihFilter(''); setTarihBitis('');
    }
    setAktifTarihBtn(tip);
  };

  const silmeyiOnaylayanRezNo = useMemo(() => {
    if (!silmeOnay) return '';
    return rezervasyonlar.find(r => r.id === silmeOnay)?.rezervasyon_no || '';
  }, [silmeOnay, rezervasyonlar]);

  const filtreli = useMemo(() => {
    let list = [...rezervasyonlar];
    if (arama) {
      const q = arama.toLowerCase();
      list = list.filter(r =>
        r.musteri_adi.toLowerCase().includes(q) ||
        r.telefon.includes(q) ||
        r.rezervasyon_no.toLowerCase().includes(q) ||
        (r.alinis_yeri || '').toLowerCase().includes(q) ||
        (r.birak_yeri || '').toLowerCase().includes(q) ||
        (r.ilce || '').toLowerCase().includes(q)
      );
    }
    if (durumFilter) list = list.filter(r => r.durum === durumFilter);
    if (ilceFilter) list = list.filter(r => r.ilce === ilceFilter);
    if (pbFilter) list = list.filter(r => r.para_birimi === pbFilter);
    if (aracFilter) list = list.filter(r => r.arac_tipi === aracFilter);
    if (tarihFilter) list = list.filter(r => r.gelis_tarihi >= tarihFilter);
    if (tarihBitis) list = list.filter(r => r.gelis_tarihi <= tarihBitis);

    list.sort((a, b) => {
      const va = String(a[siralama.kolon as keyof Rezervasyon] ?? '');
      const vb = String(b[siralama.kolon as keyof Rezervasyon] ?? '');
      return siralama.yon === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return list;
  }, [rezervasyonlar, arama, durumFilter, ilceFilter, pbFilter, aracFilter, tarihFilter, tarihBitis, siralama]);

  const selectCls = 'bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-vip-gold/40 cursor-pointer';

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">Rezervasyonlar</h2>
          <p className="text-sm text-gray-400 mt-1">
            {yukleniyor ? 'Yükleniyor...' : `${rezervasyonlar.length} toplam rezervasyon`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchRezervasyonlar}
            disabled={yukleniyor}
            className="px-3 py-2 bg-vip-dark text-gray-400 text-sm rounded-md border border-vip-gold/15 hover:text-white hover:border-vip-gold/30 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer"
          >
            <i className={`ri-refresh-line ${yukleniyor ? 'animate-spin' : ''}`}></i>
          </button>
          <a
            href="/rezervasyonlar/yeni"
            className="px-4 py-2 bg-vip-gold text-vip-black text-sm font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap text-center"
          >
            <i className="ri-add-line mr-1"></i>
            Yeni Rezervasyon
          </a>
        </div>
      </div>

      {/* Error */}
      {hata && (
        <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-sm text-red-400">{hata}</p>
          <button onClick={fetchRezervasyonlar} className="ml-auto text-xs text-red-400 underline">Tekrar dene</button>
        </div>
      )}

      {/* Quick Date Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { key: 'bugun', label: 'Bugün' },
          { key: 'hafta', label: 'Bu Hafta' },
          { key: 'ay', label: 'Bu Ay' },
          { key: 'tumü', label: 'Tümü' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setHizliTarih(key)}
            className={`px-3 py-1.5 text-xs rounded-md border transition-all whitespace-nowrap cursor-pointer ${
              aktifTarihBtn === key
                ? 'bg-vip-gold/15 text-vip-gold border-vip-gold/30'
                : 'bg-vip-dark text-gray-400 border-vip-gold/10 hover:text-white hover:border-vip-gold/20'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
            <i className="ri-search-line text-gray-500 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Müşteri, telefon, rez. no, rota..."
            value={arama}
            onChange={e => setArama(e.target.value)}
            className="w-full bg-vip-dark border border-vip-gold/15 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40 transition-colors"
          />
        </div>
        <select value={durumFilter} onChange={e => setDurumFilter(e.target.value)} className={selectCls}>
          <option value="">Tüm Durumlar</option>
          {DURUMLAR.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={ilceFilter} onChange={e => setIlceFilter(e.target.value)} className={selectCls}>
          <option value="">Tüm İlçeler</option>
          {ILCELER.filter(i => i !== 'Antalya Havalimanı').map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select value={pbFilter} onChange={e => setPbFilter(e.target.value)} className={selectCls}>
          <option value="">Para Birimi</option>
          {PARA_BIRIMLERI.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={aracFilter} onChange={e => setAracFilter(e.target.value)} className={selectCls}>
          <option value="">Araç Tipi</option>
          {ARAC_TIPLERI.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-md px-3 py-2 text-sm text-gray-400 whitespace-nowrap flex items-center">
          <span className="text-vip-gold font-semibold mr-1">{filtreli.length}</span> kayıt
        </div>
      </div>

      {/* Date Range */}
      <div className="flex flex-wrap gap-2 mb-5">
        <input
          type="date"
          value={tarihFilter}
          onChange={e => { setTarihFilter(e.target.value); setAktifTarihBtn(''); }}
          className="bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-vip-gold/40"
        />
        <span className="text-gray-500 self-center text-sm">—</span>
        <input
          type="date"
          value={tarihBitis}
          onChange={e => { setTarihBitis(e.target.value); setAktifTarihBtn(''); }}
          className="bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-vip-gold/40"
        />
        {(tarihFilter || tarihBitis) && (
          <button
            onClick={() => { setTarihFilter(''); setTarihBitis(''); setAktifTarihBtn(''); }}
            className="px-3 py-2 text-xs text-gray-500 hover:text-white bg-vip-dark border border-vip-gold/10 rounded-md transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i> Temizle
          </button>
        )}
      </div>

      {/* Table */}
      {yukleniyor ? (
        <div className="hidden md:block bg-vip-dark border border-vip-gold/10 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <RezervasyonTablosu
          rezervasyonlar={filtreli}
          siralama={siralama}
          durumYukleniyor={durumYukleniyor}
          onSiralama={handleSiralama}
          onDurumGuncelle={handleDurumGuncelle}
          onSilOnay={setSilmeOnay}
        />
      )}

      {/* Delete Confirmation Modal */}
      {silmeOnay !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-vip-dark border border-vip-gold/20 rounded-xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-500/15 border border-red-500/20 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-xl text-red-400"></i>
            </div>
            <h3 className="text-base font-semibold text-white text-center mb-1">Rezervasyonu Sil</h3>
            <p className="text-sm text-gray-400 text-center mb-1">
              <span className="text-vip-gold font-mono">{silmeyiOnaylayanRezNo}</span> numaralı rezervasyon
            </p>
            <p className="text-xs text-gray-500 text-center mb-6">kalıcı olarak silinecek. Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setSilmeOnay(null)}
                disabled={siliniyor}
                className="flex-1 py-2.5 bg-vip-black text-gray-300 text-sm font-medium rounded-md border border-vip-gold/15 hover:text-white transition-colors cursor-pointer"
              >
                İptal
              </button>
              <button
                onClick={handleSil}
                disabled={siliniyor}
                className="flex-1 py-2.5 bg-red-500 text-white text-sm font-semibold rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
              >
                {siliniyor ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-delete-bin-line"></i>}
                {siliniyor ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}